import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { ExtensionTypes, OrderingOption, QueueHistoryDisposition, QueueHistoryInput } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { QueueHistoryFilterOptions } from '../models/queue-history-filter-option';
import { QueueHistoryRegisterOptions } from '../models/queue-history-register-options';
import { getAccountQueueMembersQuery } from '../queries/account-queue-members.graphql';
import { clearQueueHistory } from '../queries/clear-queue-history.graphql';
import { ClearQueueHistory, ClearQueueHistoryVariables } from '../queries/generated/ClearQueueHistory';
import { GetAccountQueueMembers, GetAccountQueueMembersVariables } from '../queries/generated/GetAccountQueueMembers';
import { GetQueueHistory, GetQueueHistoryVariables } from '../queries/generated/GetQueueHistory';
import { getQueueHistoryQuery } from '../queries/queue-history.graphql';

@Injectable({ providedIn: 'root' })
export class QueueHistoryRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getQueueHistory(
      endCursor: string | null,
      filters: QueueHistoryFilterOptions[] | null,
      quickFilters: QueueHistoryRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetQueueHistory>> {
      const filterBuilderFilterExpression = this.constructFilterExpression(filters, quickFilters);
      const variables = this.getQueueHistoryVariables(endCursor, filterBuilderFilterExpression, orderingOptions);

      return this.apiService.query<GetQueueHistory, GetQueueHistoryVariables>({
         query: getQueueHistoryQuery,
         variables,
      });
   }

   getAccountQueueMembers(
      accountId: number,
      queueExtensionIds: number[] | null = null
   ): Observable<ExecutionResult<GetAccountQueueMembers>> {
      const filterBuilderFilterExpression: FilterExpression = {
         filterOptions: [
            {
               field: 'AccountId',
               operation: FilterOperations.EqualTo,
               value: accountId,
            },
            {
               field: 'Type',
               operation: FilterOperations.EqualTo,
               value: ExtensionTypes.CallQueue,
            },
            {
               field: 'IsArchived',
               operation: FilterOperations.EqualTo,
               value: false,
            },
         ],
      };

      if (queueExtensionIds && queueExtensionIds.length > 0) {
         filterBuilderFilterExpression.filterOptions.push({
            field: 'ExtensionId',
            operation: FilterOperations.In,
            value: queueExtensionIds,
         });
      }

      const variables: GetAccountQueueMembersVariables = {
         first: 500,
         filters: this.buildFilterExpression(filterBuilderFilterExpression),
      };

      return this.apiService.query<GetAccountQueueMembers, GetAccountQueueMembersVariables>({
         query: getAccountQueueMembersQuery,
         variables,
      });
   }

   clearQueueHistory(queueHistoryId: number) {
      const queueHistory: QueueHistoryInput = {
         callDispositionId: QueueHistoryDisposition.ManuallyCleared,
         callCompleteDateTime: new Date().toISOString(),
         queueHistoryId,
         fields: ['callDispositionId', 'callCompleteDateTime'],
      };
      return this.apiService.query<ClearQueueHistory, ClearQueueHistoryVariables>({
         query: clearQueueHistory,
         variables: { queueHistory },
      });
   }

   private getQueueHistoryVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetQueueHistoryVariables {
      const variables: GetQueueHistoryVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   private constructFilterExpression(
      filters: QueueHistoryFilterOptions[] | null,
      quickFilters: QueueHistoryRegisterOptions | null
   ): FilterExpression {
      const filterBuilderFilterExpression: FilterExpression = {
         filterOptions: filters?.map(this.convertQueueHistoryFilterOption.bind(this)) ?? [],
      };

      const searchText = quickFilters?.searchText?.trim();
      if (searchText) {
         const numericSearchText = searchText.match(/^\d+$/)?.[0];
         const searchExpression: FilterExpression = {
            logicalOperator: LogicalOperators.Or,
            filterOptions: [
               {
                  field: 'QueueExtension.Name',
                  operation: FilterOperations.Contains,
                  value: searchText,
               },
               {
                  field: 'AgentExtension.Name',
                  operation: FilterOperations.Contains,
                  value: searchText,
               },
               {
                  field: 'CallerIdName',
                  operation: FilterOperations.Contains,
                  value: searchText,
               },
               {
                  field: 'CallerIdNumber',
                  operation: FilterOperations.Contains,
                  value: searchText,
               },
            ],
         };

         if (numericSearchText) {
            searchExpression.filterOptions.push({
               field: 'QueueExtension.Number',
               operation: FilterOperations.Contains,
               value: numericSearchText,
            });
            searchExpression.filterOptions.push({
               field: 'AgentExtension.Number',
               operation: FilterOperations.Contains,
               value: numericSearchText,
            });
         }

         filterBuilderFilterExpression.subExpressions = filterBuilderFilterExpression.subExpressions
            ? [...filterBuilderFilterExpression.subExpressions, searchExpression]
            : [searchExpression];
      }

      return filterBuilderFilterExpression;
   }

   private convertQueueHistoryFilterOption(option: QueueHistoryFilterOptions): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: QueueHistoryFilterOptions): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'queueHistory':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.queueHistoryId;
                 })
               : option.value?.queueHistoryId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }
}
